import { Fragment, useEffect, useRef, useState } from 'react'
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/react'
import { useClickAnyWhere, useEventListener } from 'usehooks-ts'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslations } from '@/utils/locale'
import CtaLink, { buttonStyles } from '@/components/ui/cta-link'
import ImageContentful from '@/components/ui/image-contentful'
import SafeHTML from './safe-html'

import { getBrowserName, getOs } from '@/utils/user-info'
import { cx } from '@/utils/strings'

type Browser = {
  name: string
  url: string
  icon: string
  unicode: string
  description: string
  image: string
  imageDetails: {
    details: {
      size: number
      image: {
        width: number
        height: number
      }
    }
    fileName: string
    contentType: string
  }
  whyChooseUrl?: string
}

type DownloadTabsProps = {
  id?: string
  headline?: string
  description?: string
  browsers: Browser[]
  type?: 'browsers' | 'os'
  disclaimer?: string
}

export default function DownloadTabs({
  id,
  headline,
  description,
  browsers = [],
  type = 'browsers',
  disclaimer,
}: DownloadTabsProps) {
  const { t } = useTranslations()
  const [currentTab, setCurrentTab] = useState<string | null>(null)
  const [tabList, setTabList] = useState<Browser[]>(browsers)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!type || type === 'browsers') {
      const browser = getBrowserName()
      if (browser !== 'Unknown') {
        const browserIndex = tabList.findIndex((b) => b.name === browser)
        if (browserIndex !== -1) {
          setTabList([tabList[browserIndex], ...tabList.filter((b) => b.name !== browser)])
        }
      }
      setCurrentTab(browser)
    } else if (type === 'os') {
      const os = getOs()
      if (os !== 'Unknown') {
        const osIndex = tabList.findIndex((b) => b.name === os)
        if (osIndex !== -1) {
          setTabList([tabList[osIndex], ...tabList.filter((b) => b.name !== os)])
        }
        setCurrentTab(os)
      }
    }
  }, [])

  const [hideOutline, setHideOutline] = useState(true)
  useClickAnyWhere(() => setHideOutline(true))
  useEventListener('keydown', () => {
    setHideOutline(false)
  })

  const tabListRef = useRef<HTMLDivElement>(null)
  const motionDirection = useRef<'left' | 'right'>('left')
  const [tabIndex, setTabIndex] = useState(0)

  const handleTabChange = (index: number) => {
    motionDirection.current = index > tabIndex ? 'left' : 'right'
    setTabIndex(index)
  }

  const handleTabClick = () => {
    if (tabListRef.current) {
      const header = document.querySelector('header')
      const { y } = tabListRef.current.getBoundingClientRect()
      window.scrollBy({ top: y - (header?.offsetHeight || 0) - 12, behavior: 'smooth' })
    }
  }

  const selectedTab = tabList[tabIndex]

  return (
    <section
      id={id}
      className={cx(
        'max-w-8xl mx-auto px-5 lg:px-9 transition-opacity duration-100',
        currentTab === null ? 'opacity-0' : 'opacity-100'
      )}
    >
      {(headline || description) && (
        <div className="mb-12">
          {headline && <h3 className="text-2xl lg:text-4xl text-center mb-4">{headline}</h3>}
          {description && <p className="text-base text-center mb-0">{description}</p>}
        </div>
      )}

      <TabGroup key={currentTab} className="flex flex-col items-center" onChange={handleTabChange} manual>
        <TabList
          ref={tabListRef}
          className="bg-[#DBE5F6] dark:bg-secondary-700 dark:bg-opacity-30 rounded-3xl md:px-4 2xl:px-0 2xl:rounded-full inline-flex flex-wrap items-center justify-center max-w-[calc(clamp(40%,800px,100%))] 2xl:max-w-none mx-auto gap-1 mb-6"
        >
          {tabList.map((browser) => (
            <Tab as={Fragment} key={browser.icon}>
              {({ hover, selected }) => (
                <button
                  type="button"
                  className={cx(
                    'py-2 px-3 md:px-6 md:py-3 border-2 border-transparent rounded-full text-primaryBlue dark:text-white flex items-center transition-colors outline-hidden',
                    'focus:ring-4 focus:ring-primaryBlue focus:ring-opacity-50 dark:focus:ring-secondary-800 dark:focus:ring-opacity-30',
                    selected && `bg-primaryBlue border-primaryBlue text-white dark:bg-secondary-900 `,
                    hover && `border-primaryBlue`,
                    hideOutline && `focus:ring-0`
                  )}
                  onClick={handleTabClick}
                >
                  <SafeHTML
                    className="font-bwi leading-[2rem] text-[2rem] mr-1 md:mr-2"
                    data-name={`bwi-${browser.icon}`}
                    html={browser.unicode}
                  />
                  {browser.name}
                </button>
              )}
            </Tab>
          ))}
        </TabList>
        <TabPanels className="w-full">
          <AnimatePresence mode="wait" initial={false}>
            <TabPanel
              key={tabIndex}
              static
              className="w-full bg-secondary-50 border-secondary-100 rounded-[20px] dark:bg-secondary-900 dark:border-secondary-800"
              as={motion.div}
              initial="hide"
              animate="show"
              exit="exit"
              custom={motionDirection.current}
              variants={{
                hide: (direction: typeof motionDirection.current) => ({
                  opacity: 0,
                  x: direction === 'left' ? '20%' : '-20%',
                  transition: {
                    duration: 0.1,
                    ease: 'easeOut',
                  },
                }),
                show: {
                  opacity: 1,
                  x: 0,
                  transition: {
                    duration: 0.3,
                    ease: 'anticipate',
                  },
                },
                exit: (direction: typeof motionDirection.current) => ({
                  opacity: 0,
                  x: direction === 'left' ? '-20%' : '20%',
                  transition: {
                    duration: 0.1,
                    ease: 'easeOut',
                  },
                }),
              }}
            >
              <div className="flex flex-col-reverse md:flex-row gap-4 p-4">
                <div className="grow flex flex-col justify-center px-6 md:px-16 md:max-w-1/2">
                  <div className="flex items-center gap-3 mb-2 md:mb-6">
                    <SafeHTML
                      className="font-bwi text-[3rem]"
                      data-name={`bwi-${selectedTab.icon}`}
                      html={selectedTab.unicode}
                    />
                    <h3 className="mb-0">{selectedTab.name}</h3>
                  </div>
                  <SafeHTML className="text-lg mb-6" html={selectedTab.description} as="p" />
                  {(selectedTab.url || selectedTab.whyChooseUrl) && (
                    <div className="flex flex-col xl:flex-row gap-4">
                      {selectedTab.url && (
                        <div>
                          <CtaLink
                            className={`self-start plausible-event-name=download+click plausible-event-button=browser+${selectedTab.name.toLocaleLowerCase()}`}
                            to={selectedTab.url}
                            style={buttonStyles.btnOutlined}
                          >
                            {t('Download now')}
                          </CtaLink>
                        </div>
                      )}
                      {selectedTab.whyChooseUrl && (
                        <div>
                          <CtaLink
                            className={`self-start plausible-event-name=why-choose-bitwarden+click plausible-event-button=browser+${selectedTab.name.toLocaleLowerCase()}`}
                            to={selectedTab.whyChooseUrl}
                            style={buttonStyles.btnOutlined}
                          >
                            {t('Why Choose Bitwarden')}
                          </CtaLink>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {selectedTab.image && (
                  <div className="grow flex items-start justify-center">
                    <ImageContentful
                      publicUrl={selectedTab.image}
                      file={{ url: selectedTab.image, ...selectedTab.imageDetails }}
                      title={selectedTab.name}
                    />
                  </div>
                )}
              </div>
            </TabPanel>
          </AnimatePresence>
        </TabPanels>
      </TabGroup>
      {disclaimer && (
        <p className="text-lg text-center mt-6 mx-auto text-secondary-500 dark:text-secondary-300">{disclaimer}</p>
      )}
    </section>
  )
}
